import { IHeader } from 'components/DataTable/IHeader'
import {
    ItemCategoryLiteContract,
    ItemType,
    ProductContract,
    UOMContract,
} from 'models'
import { isNil, keyBy } from 'lodash-es'
import CheckOrClose from 'components/CheckOrClose/CheckOrClose'
import nameof from 'utilities/nameof'
import { Box, Typography } from '@mui/material'
import { t } from 'i18next'
import MonetaryFormat from 'components/MonetaryFormat/MonetaryFormat'
import calculateProfitMargin from '../utils/profitMarginCalculator'
const PRODUCT_FIELD = nameof<ProductContract>()
export function CreateProductHeader(
    categories: ItemCategoryLiteContract[],
    uoms: UOMContract[],
    hasLocationBrand: boolean,
    isMobile: boolean,
    isDepositAndCreditTable: boolean,
): IHeader[] {
    const categoryMap = keyBy(categories, (a) => a.itemCategoryID || '')
    const uomMap = keyBy(uoms, (a) => a.uomid || '')
    const picCol: IHeader = {
        label: '',
        value: (product: ProductContract) => {
            return (
                <Box alignItems="center" display="flex">
                    {!product.baseItemID && (
                        <Box
                            component="img"
                            style={{
                                height: 40,
                                width: 54,
                            }}
                            src={product.imageUrl || '/no-image.png'}
                            alt={product.name || ''}
                        />
                    )}
                </Box>
            )
        },
    }
    const itemCol: IHeader = {
        label: t('products.products.label.item'),
        value: 'name',
        sortable: true,
        isUniqueColumn: true,
    }
    const categoryCol: IHeader = {
        label: t('products.products.label.category'),
        sortable: true,
        comparator: (a: ProductContract, b: ProductContract) => {
            return (categoryMap[a.categoryID || '']?.name || '').localeCompare(
                categoryMap[b.categoryID || '']?.name || '',
            )
        },
        value: (item: ProductContract) =>
            categoryMap[item.categoryID || '']?.name ?? '',
    }
    const isActiveCol: IHeader = {
        label: t('products.products.label.active'),
        value: PRODUCT_FIELD.isActive,
        switch: true,
        sortable: true,
        hidden: (item: ProductContract) => !isNil(item.baseItemID),
        align: 'center',
        itemStyle: { textAlign: 'center' },
        width: 100,
    }
    if (isMobile) return [itemCol, categoryCol, isActiveCol]

    const refCodeCol: IHeader = {
        label: t('products.products.label.ref_code'),
        value: PRODUCT_FIELD.refCode,
        sortable: true,
        isUniqueColumn: true,
    }
    const uomCol: IHeader = {
        label: t('products.products.label.uom'),
        sortable: true,
        value: (item: ProductContract) =>
            item.uomid ? uomMap[item.uomid].symbol : '',
    }

    const costCol: IHeader = {
        label: t('products.products.label.cost', { initial: '' }),
        value: (item: ProductContract) =>
            (item.itemVariations?.length || 0) !== 0 ? (
                'VARY'
            ) : (
                <MonetaryFormat value={item.cost || 0} variant="body2" />
            ),
        sortable: true,
        comparator: (a: ProductContract, b: ProductContract) => {
            return (a.cost ?? 0) - (b.cost ?? 0)
        },
        align: 'center',
    }

    const priceCol: IHeader = {
        label: t('products.products.label.price'),
        value: (item: ProductContract) =>
            (item.itemVariations?.length || 0) !== 0 ? (
                'VARY'
            ) : (
                <MonetaryFormat value={item.basePrice ?? 0} variant="body2" />
            ),
        sortable: true,
        comparator: (a: ProductContract, b: ProductContract) => {
            return (a.basePrice ?? 0) - (b.basePrice ?? 0)
        },
        align: 'center',
    }

    const depositPriceCol: IHeader = {
        label: t('products.products.label.deposit_price'),
        helpText: t('products.products.helper.deposit_price'),
        value: (item: ProductContract) => {
            var depositItem = item.depositAndCreditItemVariations?.find(
                (i) => i.type === ItemType.DepositProduct,
            )

            return depositItem?.isActive ?? false ? (
                <MonetaryFormat
                    value={depositItem?.basePrice ?? 0}
                    variant="body2"
                />
            ) : (
                '-'
            )
        },
        sortable: true,
        comparator: (a: ProductContract, b: ProductContract) => {
            return (a.basePrice ?? 0) - (b.basePrice ?? 0)
        },
        align: 'center',
    }

    const creditPriceCol: IHeader = {
        label: t('products.products.label.credit_price'),
        helpText: t('products.products.helper.credit_price'),
        value: (item: ProductContract) => {
            var creditItem = item.depositAndCreditItemVariations?.find(
                (i) => i.type === ItemType.CreditProduct,
            )

            return creditItem?.isActive ?? false ? (
                <MonetaryFormat
                    value={Math.abs(creditItem?.basePrice ?? 0)}
                    variant="body2"
                    color={'error.main'}
                />
            ) : (
                '-'
            )
        },
        sortable: true,
        comparator: (a: ProductContract, b: ProductContract) => {
            return (a.basePrice ?? 0) - (b.basePrice ?? 0)
        },
        align: 'center',
    }

    const salesTaxCol: IHeader = {
        label: t('products.products.label.sales_tax'),
        value: (item: ProductContract) => {
            if (item.tax) {
                return `${item.tax.rate}%`
            } else return '-'
        },
        align: 'center',
    }

    const taxInclusiveCol: IHeader = {
        label: t('products.products.label.tax_inclusive'),
        sortable: true,
        comparator: (a: ProductContract, b: ProductContract) => {
            if (a.isTaxInclusive) {
                return -1
            } else return 1
        },
        value: (item: ProductContract) => (
            <CheckOrClose check={item.isTaxInclusive || false} />
        ),
        align: 'center',
        itemStyle: { textAlign: 'center' },
    }

    const profitMarginCol: IHeader = {
        label: t('products.products.label.profit_margin'),
        value: (item: ProductContract) => {
            if ((item.itemVariations?.length || 0) !== 0) return 'VARY'

            var profit = calculateProfitMargin(
                item.basePrice ?? 0,
                item.cost ?? 0,
                item.tax?.rate,
                item.isTaxInclusive,
            )

            if (typeof profit === 'number') {
                const profitMargin =
                    profit < 0 ? `(${Math.abs(profit)}%)` : profit + '%'

                return (
                    <Typography color={profit < 0 ? 'error' : 'primary'}>
                        {profitMargin}
                    </Typography>
                )
            }

            return <Typography>{'N/A'}</Typography>
        },
        sortable: true,
    }

    const isAvailableCol: IHeader = {
        label: t('products.products.label.available'),
        name: 'isAvailableForSelection',
        helpText: 'Available At this Location',
        value: PRODUCT_FIELD.isAvailableForSelection,
        switch: true,
        sortable: true,
        hidden: (item: ProductContract) =>
            !isNil(item.baseItemID) || !item.isActive,
        align: 'center',
        itemStyle: { textAlign: 'center' },
    }

    let header: IHeader[]
    if (isDepositAndCreditTable)
        header = [
            picCol,
            itemCol,
            refCodeCol,
            categoryCol,
            uomCol,
            depositPriceCol,
            creditPriceCol,
            isAvailableCol,
            isActiveCol,
        ]
    else
        header = [
            picCol,
            itemCol,
            refCodeCol,
            categoryCol,
            uomCol,
            costCol,
            priceCol,
            salesTaxCol,
            taxInclusiveCol,
            profitMarginCol,
            isAvailableCol,
            isActiveCol,
        ]
    if (hasLocationBrand) {
        let brand: IHeader = {
            label: t('products.products.label.brand'),
            value: PRODUCT_FIELD.brandName,
            sortable: true,
        }
        let index = header.findIndex((a) => a.label === 'Category')
        header.splice(index, 0, brand)
    }
    return header
}
